import { Grid, Typography } from '@material-ui/core';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import { useScrollButtonState } from '@ncr-retail/hooks';
import React from 'react';
import InteractiveDialogContent, {
  DialogBar,
} from 'src/common/components/interactive-dialogue/components/interactive-dialog-content/interactive-dialog-content';
import InteractiveDialogueContainer from 'src/common/components/interactive-dialogue/components/interactive-dialogue-container';
import { pushEmeraldModal } from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { useTranslation } from 'react-i18next';
import { Pagination } from 'src/pagination/pagination';
import { styles } from './remove-items-categories-modal-styles';
import { VerticalAlignment } from 'src/common/enumerations/verticalAlignment';
import clsx from 'clsx';
import { VoidInterventionReason } from 'src/interventions/business-logic/void-intervention-rejected-items';

export const RemoveRejectedItemsWithCategoriesModalId = 'RemoveRejectedItemsWithCategoriesModal';

export const showRemoveRejectedItemsWithCategoriesModal = (
  props: RemoveRejectedItemsWithCategoriesProps,
): void => {
  pushEmeraldModal(
    <RemoveRejectedItemsWithCategoriesModal {...props} />,
    undefined,
    RemoveRejectedItemsWithCategoriesModalId,
  );
};

const TextContent: React.FC<RemoveRejectedItemsWithCategoriesProps> = ({
  itemCategories,
}: RemoveRejectedItemsWithCategoriesProps) => {
  const classes = styles();
  const [{ currentPage, lastPage, onPageChanged }, scrollRef] = useScrollButtonState();
  const { t } = useTranslation();

  return (
    <Grid
      container
      justifyContent='center'
      direction='column'
      className={classes.rejectedItemsContainer}
    >
      <Grid
        item
        container
        direction='column'
        justifyContent='center'
        className={classes.rejectedItemsContainer}
      >
        <Typography variant={'h2'} className={classes.title}>
          {t('PleaseCollectItems')}
        </Typography>
        <DialogBar type={EmeraldDialogTypes.Warning} />
        <Grid data-testid='remove-items' className={`${classes.voidedItems}`}>
          <Grid
            container
            justifyContent='center'
            ref={scrollRef}
            className={`verticalScroll fullHeight`}
            data-testid='remove-items-scroll'
          >
            <Grid
              container
              direction='row'
              justifyContent={itemCategories.length > 1 ? 'flex-start' : 'center'}
            >
              {itemCategories.map(({ category, itemDescriptions }: ItemCategory, idx) => (
                <Grid
                  key={category}
                  xs={
                    itemCategories.length === 1 ||
                    (itemCategories.length - 1 === idx && idx % 2 === 0)
                      ? 12
                      : 6
                  }
                  container
                  justifyContent={itemCategories.length > 1 ? 'flex-start' : 'center'}
                  direction='row'
                  className={clsx(
                    classes.categoryContainer,
                    idx % 2 === 0 ? classes.rightPadding : classes.leftPadding,
                  )}
                >
                  <ReportProblemOutlinedIcon className={classes.icon} />

                  <Grid item direction='column'>
                    <Grid container>
                      <Typography className={clsx(classes.category, classes.noOverflow)}>
                        {t(category)}
                      </Typography>
                    </Grid>

                    {itemDescriptions.map((item, idx) =>
                      item && item.length > 0 ? (
                        <Grid key={idx} container>
                          <Typography className={clsx(classes.messageLine, classes.noOverflow)}>
                            {`\u2022 ${item}`}
                          </Typography>
                        </Grid>
                      ) : (
                        <></>
                      ),
                    )}
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item container className={`${classes.arrows}`} justifyContent='space-evenly'>
            <Pagination
              currentPage={currentPage}
              totalPages={lastPage}
              updateCurrentPageHandler={onPageChanged}
              displayText={false}
              testIdPrefix={'remove-items-modal'}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const RemoveRejectedItemsWithCategoriesModal = (
  props: RemoveRejectedItemsWithCategoriesProps,
): JSX.Element => {
  const classes = styles();
  const { onClose } = props;
  return (
    <InteractiveDialogueContainer
      open
      hideCancelButton
      acceptButtonLabel={'Ok'}
      handleClose={onClose}
      handleAccept={onClose}
      dialogWidth={64.7}
      dialogMinWidthFlag={true}
      classes={{ dialogPaperFullWidth: classes.dialogPaperFullWidth }}
      dialogTestIdPrefix='remove-rejected-items-categories-modal'
      component={
        <InteractiveDialogContent
          type={EmeraldDialogTypes.Warning}
          subMessage=''
          verticalAlignment={VerticalAlignment.Center}
          textContent={<TextContent itemCategories={props.itemCategories} onClose={onClose} />}
        />
      }
    />
  );
};

export interface ItemCategory {
  category: VoidInterventionReason;
  itemDescriptions: string[];
}

export interface RemoveRejectedItemsWithCategoriesProps {
  itemCategories: ItemCategory[];
  onClose: () => void;
}
